import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PiggyBank, Wallet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { payrollAPI } from '../../services/api';

/**
 * End-of-service gratuity (EOSB) employer-cost report — Gulf only.
 * Shows the monthly provision and the total accrued liability per employee.
 */
const PayrollEosbReport = ({ periodId, formatCurrency }) => {
  const { t } = useTranslation();
  const { data, isPending, isError } = useQuery({
    queryKey: ['payroll-eosb-report', periodId],
    queryFn: () => payrollAPI.getEosbReport(periodId),
    enabled: !!periodId,
  });

  if (isPending) {
    return (
      <Card>
        <CardContent className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary" />
        </CardContent>
      </Card>
    );
  }

  if (isError || !data || data.rows.length === 0) {
    return null; // nothing to show for non-Gulf periods or no EOSB data
  }

  const fmt = (v) => (formatCurrency ? formatCurrency(v) : `${v.toLocaleString()} ${data.currency}`);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PiggyBank className="h-5 w-5" />
          {t('payrollGulf.eosbTitle')}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {t('payrollGulf.eosbSubtitle', { count: data.employee_count })}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="rounded-lg border p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Wallet className="h-4 w-4" /> {t('payrollGulf.eosbMonthly')}
            </div>
            <div className="text-2xl font-bold mt-1">{fmt(data.total_monthly_accrual)}</div>
          </div>
          <div className="rounded-lg border p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <PiggyBank className="h-4 w-4" /> {t('payrollGulf.eosbLiability')}
            </div>
            <div className="text-2xl font-bold mt-1">{fmt(data.total_liability)}</div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="py-2 pr-4 font-medium">{t('payrollGulf.eosbColEmployee')}</th>
                <th className="py-2 pr-4 font-medium">{t('payrollGulf.eosbColReg')}</th>
                <th className="py-2 pr-4 font-medium">{t('payrollGulf.eosbColDept')}</th>
                <th className="py-2 pr-4 font-medium text-right">{t('payrollGulf.eosbColMonthly')}</th>
                <th className="py-2 font-medium text-right">{t('payrollGulf.eosbColAccrued')}</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.employee_id} className="border-b last:border-b-0">
                  <td className="py-2 pr-4">{r.employee_name}</td>
                  <td className="py-2 pr-4 text-muted-foreground">{r.registration_number}</td>
                  <td className="py-2 pr-4 text-muted-foreground">{r.department || '—'}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{fmt(r.monthly_accrual)}</td>
                  <td className="py-2 text-right tabular-nums font-medium">{fmt(r.accrued_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default PayrollEosbReport;
